/* eslint no-underscore-dangle: ["error", { "allow": ["_id", "__v"] }] */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useAdminApi from '../../service/adminService';
import { money } from './fields';
import useOrganizations from '../../service/useOrganizations';
import { formatDate } from '../../service/dateFormatting';

export default function OrderList() {
    const api = useAdminApi();
    const [params, setParams] = useSearchParams();
    const [result, setResult] = useState({
        items: [], total: 0, pages: 1, page: 1,
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const { organizations, error: organizationsError } = useOrganizations(api);
    const [orgQuery, setOrgQuery] = useState('');
    const queryString = params.toString();
    const change = (key, value) => {
        const next = new URLSearchParams(params);
        next.set(key, value);
        if (key !== 'page') next.set('page', '1');
        setParams(next, { replace: true });
    };
    useEffect(() => {
        let active = true;
        setLoading(true);
        const timer = setTimeout(() => {
            api(`/orders?${queryString}`)
                .then((data) => {
                    if (active) {
                        setResult(data);
                        setError('');
                    }
                })
                .catch((e) => {
                    if (active) setError(e.message);
                })
                .finally(() => {
                    if (active) setLoading(false);
                });
        }, 250);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [api, queryString]);
    const organizationId = params.get('organizationId') || '';
    const visibleOrganizations = organizations.filter((organization) => (
        organization._id === organizationId
        || organization.name.toLocaleLowerCase().includes(orgQuery.toLocaleLowerCase())
    ));
    const sort = (key) => {
        const next = new URLSearchParams(params);
        next.set('sort', key);
        next.set(
            'direction',
            params.get('sort') === key && params.get('direction') === 'asc' ? 'desc' : 'asc',
        );
        next.set('page', '1');
        setParams(next);
    };
    return (
        <div>
            <section className="po-panel po-filter-panel">
                <div className="po-section-heading">
                    <div>
                        <h2>Find orders</h2>
                        <p>
                            Search order records, then narrow the results by organization,
                            status, or date.
                        </p>
                    </div>
                </div>
                <div className="po-filter-layout">
                    <section className="po-filter-group">
                        <h3>Order or organization</h3>
                        <label
                            htmlFor="orderlist-field-1"
                            className="po-field">
                            <span>Search orders</span>
                            <input
                                id="orderlist-field-1"
                                type="search"
                                placeholder="PO, customer, email, organization…"
                                value={params.get('q') || ''}
                                onChange={(e) => change('q', e.target.value)} />
                        </label>
                        <div className="po-grid">
                            <label
                                htmlFor="orderlist-field-3"
                                className="po-field">
                                <span>Find organization</span>
                                <input
                                    id="orderlist-field-3"
                                    type="search"
                                    placeholder="Type to narrow the list"
                                    value={orgQuery}
                                    onChange={(e) => setOrgQuery(e.target.value)} />
                            </label>
                            <label
                                htmlFor="orderlist-field-4"
                                className="po-field">
                                <span>Organization</span>
                                <select
                                    id="orderlist-field-4"
                                    value={organizationId}
                                    onChange={(e) => change('organizationId', e.target.value)}>
                                    <option value="">All organizations</option>
                                    {organizationId && !organizations.some((org) => (
                                        org._id === organizationId
                                    )) && (
                                        <option value={organizationId}>
                                            Selected organization
                                        </option>
                                    )}
                                    {visibleOrganizations.map((org) => (
                                        <option
                                            key={org._id}
                                            value={org._id}>
                                            {org.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    </section>
                    <section className="po-filter-group">
                        <h3>Status and dates</h3>
                        <label
                            htmlFor="orderlist-field-2"
                            className="po-field">
                            <span>Payment status</span>
                            <select
                                id="orderlist-field-2"
                                value={params.get('status') || ''}
                                onChange={(e) => change('status', e.target.value)}>
                                <option value="">All statuses</option>
                                <option value="unpaid">Awaiting payment</option>
                                <option value="paid">Paid</option>
                            </select>
                        </label>
                        <div className="po-grid">
                            <label
                                htmlFor="orderlist-field-5"
                                className="po-field">
                                <span>Created from</span>
                                <input
                                    id="orderlist-field-5"
                                    type="date"
                                    value={params.get('from') || ''}
                                    onChange={(e) => change('from', e.target.value)} />
                            </label>
                            <label
                                htmlFor="orderlist-field-6"
                                className="po-field">
                                <span>Created through</span>
                                <input
                                    id="orderlist-field-6"
                                    type="date"
                                    value={params.get('to') || ''}
                                    onChange={(e) => change('to', e.target.value)} />
                            </label>
                        </div>
                    </section>
                </div>
            </section>
            {(error || organizationsError) && (
                <p
                    className="po-error"
                    role="alert">
                    {error || organizationsError}
                </p>
            )}
            <section className="po-panel po-results-panel">
                <div className="po-results-heading">
                    <div>
                        <h2>Purchase orders</h2>
                        <p role="status">
                            {loading ? 'Loading orders…' : `${result.total} orders`}
                        </p>
                    </div>
                </div>
                <div className="po-table-wrap">
                    <table aria-label="Purchase orders">
                        <thead>
                            <tr>
                                {[
                                    ['poNumber', 'PO / organization'],
                                    ['customerName', 'Customer'],
                                    ['amountCents', 'Amount'],
                                    ['createdAt', 'Created'],
                                    ['paymentStatus', 'Status'],
                                ].map(([key, label]) => {
                                    let direction = 'none';
                                    if ((params.get('sort') || 'createdAt') === key) {
                                        direction = params.get('direction') === 'asc'
                                            ? 'ascending'
                                            : 'descending';
                                    }
                                    return (
                                        <th
                                            key={key}
                                            scope="col"
                                            aria-sort={direction}>
                                            <button
                                                type="button"
                                                onClick={() => sort(key)}>
                                                {label}
                                                {direction === 'ascending' ? ' ↑' : ''}
                                                {direction === 'descending' ? ' ↓' : ''}
                                            </button>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {result.items.map((order) => (
                                <tr key={order._id}>
                                    <td>
                                        <Link to={`/admin/orders/${order._id}`}>
                                            {order.poNumber}
                                        </Link>
                                        <small>{order.organizationName}</small>
                                    </td>
                                    <td>
                                        {order.customerName}
                                        <small>{order.customerEmail}</small>
                                    </td>
                                    <td>{money(order.amountCents)}</td>
                                    <td>{formatDate(order.createdAt)}</td>
                                    <td>
                                        {order.paymentStatus === 'paid'
                                            ? 'Paid'
                                            : 'Awaiting payment'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {!loading && !error && !result.items.length && (
                    <p>No orders match these filters.</p>
                )}
                <div className="po-actions">
                    <button
                        type="button"
                        disabled={loading || result.page <= 1}
                        onClick={() => change('page', String(result.page - 1))}>
                        Previous
                    </button>
                    <span>
                        Page
                        {' '}
                        {result.page}
                        {' '}
                        of
                        {' '}
                        {result.pages}
                    </span>
                    <button
                        type="button"
                        disabled={loading || result.page >= result.pages}
                        onClick={() => change('page', String(result.page + 1))}>
                        Next
                    </button>
                </div>
            </section>
        </div>
    );
}
